import { Component, OnInit, Input, Output, EventEmitter } from '@angular/core';
import { MigrationProcessingDataEntity } from '../../../models/migration-processing-data-entity';
import { MigrationRoutingInfo } from '../../../models/migration-routing-info';
import { DataService } from '../../../services/data.service';
import { faCaretUp, faCaretDown } from '@fortawesome/free-solid-svg-icons';

@Component({
  selector: 'app-migration-processing-data-entities',
  templateUrl: './migration-processing-data-entities.component.html',
  styleUrls: ['./migration-processing-data-entities.component.scss'],
  standalone: false
})
export class MigrationProcessingDataEntitiesComponent implements OnInit {
  @Input() migrationProcessingId: string;
  @Output() entitiesChanged = new EventEmitter<void>();

  entities: MigrationProcessingDataEntity[] = [];
  selectedEntities: Set<string> = new Set<string>();
  bulkTargetActivity: string = '';
  selectedSection: string = '';
  moveAfterSection: string = '';
  moveBeforeSection: string = '';
  uniqueSections: string[] = [];
  loading = false;
  error: string = null;

  // Routing-related properties
  activeTab: string = 'entities';
  routingInfo: MigrationRoutingInfo = null;
  loadingRouting = false;

  // FontAwesome icons
  faCaretUp = faCaretUp;
  faCaretDown = faCaretDown;

  constructor(private dataSvc: DataService) {}

  ngOnInit() {
    if (this.migrationProcessingId) {
      this.loadEntities();
    }
  }

  loadEntities() {
    this.loading = true;
    this.error = null;
    this.dataSvc
      .getMigrationProcessingDataEntities(this.migrationProcessingId)
      .subscribe(
        data => {
          // Initialize helper properties for UI state
          this.entities = data.map(entity => ({
            ...entity,
            expanded: false,
            childrenExpanded: false
          }));
          this.updateUniqueSections();
          this.loading = false;
        },
        error => {
          this.error = 'Error loading data entities: ' + error;
          this.loading = false;
        }
      );
  }

  updateUniqueSections() {
    const sections = new Set<string>();
    this.entities.forEach(entity => {
      if (entity.section) {
        sections.add(entity.section);
      }
    });
    this.uniqueSections = Array.from(sections).sort();
  }

  parseMetadata() {
    if (
      confirm(
        'This will replace all existing data entities and routes with data from the metadata file. Continue?'
      )
    ) {
      this.loading = true;
      this.error = null;
      this.dataSvc.parseMetadataFile(this.migrationProcessingId).subscribe(
        result => {
          // Initialize helper properties for UI state
          this.entities = (result.dataEntities || []).map(entity => ({
            ...entity,
            expanded: false,
            childrenExpanded: false
          }));

          // Display validation warnings and errors
          if (result.validationErrors && result.validationErrors.length > 0) {
            const errorMessage =
              'Metadata validation errors:\n\n' +
              result.validationErrors.join('\n');
            alert(errorMessage);
            this.error = `Found ${result.validationErrors.length} validation error(s). See console for details.`;
            console.error(
              'Metadata validation errors:',
              result.validationErrors
            );
          }

          if (
            result.validationWarnings &&
            result.validationWarnings.length > 0
          ) {
            console.warn(
              'Metadata validation warnings:',
              result.validationWarnings
            );
          }

          this.loading = false;
          this.entitiesChanged.emit();
          // Clear cached routing info so it reloads
          this.routingInfo = null;
        },
        error => {
          this.error = 'Error parsing metadata: ' + error;
          this.loading = false;
        }
      );
    }
  }

  saveEntityField(entity: MigrationProcessingDataEntity) {
    this.error = null;
    this.dataSvc.saveMigrationProcessingDataEntity(entity).subscribe(
      data => {
        const index = this.entities.findIndex(e => e.id === data.id);
        if (index >= 0) {
          // Preserve UI state (expanded, childrenExpanded) when updating from backend
          this.entities[index] = {
            ...data,
            expanded: this.entities[index].expanded,
            childrenExpanded: this.entities[index].childrenExpanded
          };
        }
        this.entitiesChanged.emit();
      },
      error => {
        this.error = 'Error saving entity: ' + error;
        this.loadEntities(); // Reload to revert changes
      }
    );
  }

  moveUp(entity: MigrationProcessingDataEntity, index: number) {
    if (index === 0) return;

    const temp = entity.displayOrder;
    entity.displayOrder = this.entities[index - 1].displayOrder;
    this.entities[index - 1].displayOrder = temp;

    this.dataSvc.saveMigrationProcessingDataEntity(entity).subscribe();
    this.dataSvc
      .saveMigrationProcessingDataEntity(this.entities[index - 1])
      .subscribe(() => {
        this.loadEntities();
      });
  }

  moveDown(entity: MigrationProcessingDataEntity, index: number) {
    if (index === this.entities.length - 1) return;

    const temp = entity.displayOrder;
    entity.displayOrder = this.entities[index + 1].displayOrder;
    this.entities[index + 1].displayOrder = temp;

    this.dataSvc.saveMigrationProcessingDataEntity(entity).subscribe();
    this.dataSvc
      .saveMigrationProcessingDataEntity(this.entities[index + 1])
      .subscribe(() => {
        this.loadEntities();
      });
  }

  toggleSelection(entity: MigrationProcessingDataEntity) {
    if (this.selectedEntities.has(entity.id)) {
      this.selectedEntities.delete(entity.id);
    } else {
      this.selectedEntities.add(entity.id);
    }
  }

  toggleSelectAll(event: any) {
    if (event.target.checked) {
      this.entities.forEach(entity => this.selectedEntities.add(entity.id));
    } else {
      this.selectedEntities.clear();
    }
  }

  isAllSelected(): boolean {
    return (
      this.entities.length > 0 &&
      this.selectedEntities.size === this.entities.length
    );
  }

  selectBySection() {
    if (!this.selectedSection) return;

    this.entities
      .filter(entity => entity.section === this.selectedSection)
      .forEach(entity => this.selectedEntities.add(entity.id));
  }

  clearSelection() {
    this.selectedEntities.clear();
    this.bulkTargetActivity = '';
    this.moveAfterSection = '';
    this.moveBeforeSection = '';
  }

  moveSelectedAfterSection() {
    if (!this.moveAfterSection || this.selectedEntities.size === 0) return;

    this.loading = true;
    this.error = null;

    // Find the last entity in the target section
    const sectionEntities = this.entities.filter(
      e => e.section === this.moveAfterSection
    );
    if (sectionEntities.length === 0) {
      this.error = 'Target section not found';
      this.loading = false;
      return;
    }

    // Get the highest display order in the target section
    const maxDisplayOrderInSection = Math.max(
      ...sectionEntities.map(e => e.displayOrder)
    );

    // Get selected entities sorted by their current display order
    const selectedEntitiesList = this.entities
      .filter(e => this.selectedEntities.has(e.id))
      .sort((a, b) => a.displayOrder - b.displayOrder);

    // Get all entities that need reordering
    const entitiesToUpdate: MigrationProcessingDataEntity[] = [];

    // Calculate new display orders
    let newDisplayOrder = maxDisplayOrderInSection + 1;
    const displayOrderMap = new Map<string, number>();

    // Assign new display orders to selected items (placing them after the target section)
    selectedEntitiesList.forEach(entity => {
      displayOrderMap.set(entity.id, newDisplayOrder);
      newDisplayOrder++;
    });

    // Shift remaining items that come after the insertion point
    this.entities.forEach(entity => {
      if (
        !this.selectedEntities.has(entity.id) &&
        entity.displayOrder > maxDisplayOrderInSection
      ) {
        displayOrderMap.set(entity.id, newDisplayOrder);
        newDisplayOrder++;
      }
    });

    // Apply new display orders
    displayOrderMap.forEach((newOrder, entityId) => {
      const entity = this.entities.find(e => e.id === entityId);
      if (entity) {
        entity.displayOrder = newOrder;
        entitiesToUpdate.push(entity);
      }
    });

    // Save all updated entities
    if (entitiesToUpdate.length > 0) {
      this.saveBulkReorder(entitiesToUpdate);
    } else {
      this.loading = false;
    }
  }

  moveSelectedBeforeSection() {
    if (!this.moveBeforeSection || this.selectedEntities.size === 0) return;

    this.loading = true;
    this.error = null;

    // Find the first entity in the target section
    const sectionEntities = this.entities.filter(
      e => e.section === this.moveBeforeSection
    );
    if (sectionEntities.length === 0) {
      this.error = 'Target section not found';
      this.loading = false;
      return;
    }

    // Get the lowest display order in the target section
    const minDisplayOrderInSection = Math.min(
      ...sectionEntities.map(e => e.displayOrder)
    );

    // Get selected entities sorted by their current display order
    const selectedEntitiesList = this.entities
      .filter(e => this.selectedEntities.has(e.id))
      .sort((a, b) => a.displayOrder - b.displayOrder);

    // Get all entities that need reordering
    const entitiesToUpdate: MigrationProcessingDataEntity[] = [];

    // Calculate new display orders
    const displayOrderMap = new Map<string, number>();

    // Start numbering from the beginning
    let newDisplayOrder = 0;

    // First, assign display orders to items that come before the target section
    this.entities.forEach(entity => {
      if (
        !this.selectedEntities.has(entity.id) &&
        entity.displayOrder < minDisplayOrderInSection
      ) {
        displayOrderMap.set(entity.id, newDisplayOrder);
        newDisplayOrder++;
      }
    });

    // Then, insert selected items (placing them before the target section)
    selectedEntitiesList.forEach(entity => {
      displayOrderMap.set(entity.id, newDisplayOrder);
      newDisplayOrder++;
    });

    // Finally, add the target section and everything after it
    this.entities.forEach(entity => {
      if (
        !this.selectedEntities.has(entity.id) &&
        entity.displayOrder >= minDisplayOrderInSection
      ) {
        displayOrderMap.set(entity.id, newDisplayOrder);
        newDisplayOrder++;
      }
    });

    // Apply new display orders
    displayOrderMap.forEach((newOrder, entityId) => {
      const entity = this.entities.find(e => e.id === entityId);
      if (entity) {
        entity.displayOrder = newOrder;
        entitiesToUpdate.push(entity);
      }
    });

    // Save all updated entities
    if (entitiesToUpdate.length > 0) {
      this.saveBulkReorder(entitiesToUpdate);
    } else {
      this.loading = false;
    }
  }

  applyBulkUpdate() {
    if (!this.bulkTargetActivity || this.selectedEntities.size === 0) return;

    this.loading = true;
    this.error = null;

    const selectedEntitiesList = this.entities.filter(e =>
      this.selectedEntities.has(e.id)
    );
    let updateCount = 0;

    selectedEntitiesList.forEach(entity => {
      entity.targetActivity = this.bulkTargetActivity;
      this.dataSvc.saveMigrationProcessingDataEntity(entity).subscribe(
        data => {
          updateCount++;
          if (updateCount === selectedEntitiesList.length) {
            this.loading = false;
            this.clearSelection();
            this.loadEntities();
            this.entitiesChanged.emit();
          }
        },
        error => {
          this.error = 'Error updating entity: ' + error;
          this.loading = false;
        }
      );
    });
  }

  moveSelectedUp() {
    if (this.selectedEntities.size === 0) return;

    // Get all selected entities sorted by their current display order
    const selectedEntitiesList = this.entities
      .filter(e => this.selectedEntities.has(e.id))
      .sort((a, b) => a.displayOrder - b.displayOrder);

    // Can't move up if the first selected item is at position 0
    const firstSelectedIndex = this.entities.findIndex(
      e => e.id === selectedEntitiesList[0].id
    );
    if (firstSelectedIndex === 0) return;

    // Build a map of current positions
    const entityOrderMap = new Map<string, number>();
    this.entities.forEach((entity, index) => {
      entityOrderMap.set(entity.id, index);
    });

    const entitiesToUpdate: MigrationProcessingDataEntity[] = [];
    const newOrderMap = new Map<string, number>();

    // For each selected entity, find the unselected entity immediately above it
    selectedEntitiesList.forEach(selectedEntity => {
      const currentIndex = entityOrderMap.get(selectedEntity.id);

      // Find the nearest unselected item above
      let targetIndex = currentIndex - 1;
      while (
        targetIndex >= 0 &&
        this.selectedEntities.has(this.entities[targetIndex].id)
      ) {
        targetIndex--;
      }

      // If we found an unselected item above, swap with it
      if (targetIndex >= 0) {
        const targetEntity = this.entities[targetIndex];

        // Record new display orders
        if (!newOrderMap.has(selectedEntity.id)) {
          newOrderMap.set(selectedEntity.id, targetEntity.displayOrder);
        }
        if (!newOrderMap.has(targetEntity.id)) {
          newOrderMap.set(targetEntity.id, selectedEntity.displayOrder);
        }
      }
    });

    // Apply new display orders
    newOrderMap.forEach((newOrder, entityId) => {
      const entity = this.entities.find(e => e.id === entityId);
      if (entity) {
        entity.displayOrder = newOrder;
        entitiesToUpdate.push(entity);
      }
    });

    // Save all updated entities
    if (entitiesToUpdate.length > 0) {
      this.saveBulkReorder(entitiesToUpdate);
    }
  }

  moveSelectedDown() {
    if (this.selectedEntities.size === 0) return;

    // Get all selected entities sorted by their current display order (descending)
    const selectedEntitiesList = this.entities
      .filter(e => this.selectedEntities.has(e.id))
      .sort((a, b) => b.displayOrder - a.displayOrder);

    // Can't move down if the last selected item is at the end
    const lastSelectedIndex = this.entities.findIndex(
      e => e.id === selectedEntitiesList[0].id
    );
    if (lastSelectedIndex === this.entities.length - 1) return;

    // Build a map of current positions
    const entityOrderMap = new Map<string, number>();
    this.entities.forEach((entity, index) => {
      entityOrderMap.set(entity.id, index);
    });

    const entitiesToUpdate: MigrationProcessingDataEntity[] = [];
    const newOrderMap = new Map<string, number>();

    // For each selected entity, find the unselected entity immediately below it
    selectedEntitiesList.forEach(selectedEntity => {
      const currentIndex = entityOrderMap.get(selectedEntity.id);

      // Find the nearest unselected item below
      let targetIndex = currentIndex + 1;
      while (
        targetIndex < this.entities.length &&
        this.selectedEntities.has(this.entities[targetIndex].id)
      ) {
        targetIndex++;
      }

      // If we found an unselected item below, swap with it
      if (targetIndex < this.entities.length) {
        const targetEntity = this.entities[targetIndex];

        // Record new display orders
        if (!newOrderMap.has(selectedEntity.id)) {
          newOrderMap.set(selectedEntity.id, targetEntity.displayOrder);
        }
        if (!newOrderMap.has(targetEntity.id)) {
          newOrderMap.set(targetEntity.id, selectedEntity.displayOrder);
        }
      }
    });

    // Apply new display orders
    newOrderMap.forEach((newOrder, entityId) => {
      const entity = this.entities.find(e => e.id === entityId);
      if (entity) {
        entity.displayOrder = newOrder;
        entitiesToUpdate.push(entity);
      }
    });

    // Save all updated entities
    if (entitiesToUpdate.length > 0) {
      this.saveBulkReorder(entitiesToUpdate);
    }
  }

  saveBulkReorder(entities: MigrationProcessingDataEntity[]) {
    this.loading = true;
    this.error = null;

    // Remove duplicates
    const uniqueEntities = Array.from(
      new Map(entities.map(e => [e.id, e])).values()
    );
    let saveCount = 0;

    uniqueEntities.forEach(entity => {
      this.dataSvc.saveMigrationProcessingDataEntity(entity).subscribe(
        () => {
          saveCount++;
          if (saveCount === uniqueEntities.length) {
            this.loading = false;
            this.loadEntities();
            this.entitiesChanged.emit();
          }
        },
        error => {
          this.error = 'Error reordering entities: ' + error;
          this.loading = false;
          this.loadEntities();
        }
      );
    });
  }

  onRoutingTabClick() {
    this.activeTab = 'routing';
    if (!this.routingInfo) {
      this.loadRoutingInfo();
    }
  }

  loadRoutingInfo() {
    if (!this.migrationProcessingId) return;

    this.loadingRouting = true;
    this.error = null;

    this.dataSvc.getMigrationRoutingInfo(this.migrationProcessingId).subscribe({
      next: data => {
        this.routingInfo = data;
        this.loadingRouting = false;
      },
      error: err => {
        this.error =
          'Error loading routing information: ' +
          (err.error?.message || err.message);
        this.loadingRouting = false;
      }
    });
  }

  // Conditional Configuration Helper Methods

  hasConditionalConfig(entity: MigrationProcessingDataEntity): boolean {
    if (!entity) return false;
    return (
      this.hasConditionallyVisible(entity) ||
      this.hasConditionallyRequired(entity)
    );
  }

  hasConditionallyVisible(entity: MigrationProcessingDataEntity): boolean {
    if (!entity) return false;
    const json = entity.conditionallyVisibleJson;
    return !!(json && json !== 'null' && json.trim() !== '');
  }

  hasConditionallyRequired(entity: MigrationProcessingDataEntity): boolean {
    if (!entity) return false;
    const json = entity.conditionallyRequiredJson;
    return !!(json && json !== 'null' && json.trim() !== '');
  }

  toggleExpand(entity: MigrationProcessingDataEntity): void {
    if (entity) {
      entity.expanded = !entity.expanded;
    }
  }

  parseConditions(conditionsJson: string): any[] {
    if (
      !conditionsJson ||
      conditionsJson.trim() === '' ||
      conditionsJson.trim() === 'null'
    ) {
      return [];
    }

    try {
      const conditions = JSON.parse(conditionsJson);
      return Array.isArray(conditions) ? conditions : [];
    } catch (error) {
      console.error(
        'Error parsing conditions JSON:',
        error,
        'Input:',
        conditionsJson
      );
      return [];
    }
  }

  getConditionallyVisibleJson(entity: MigrationProcessingDataEntity): string {
    return entity.conditionallyVisibleJson || '';
  }

  getConditionallyRequiredJson(entity: MigrationProcessingDataEntity): string {
    return entity.conditionallyRequiredJson || '';
  }

  getConditionallyVisibleOperator(
    entity: MigrationProcessingDataEntity
  ): string {
    return entity.conditionallyVisibleLogicalOperator || '';
  }

  getConditionallyRequiredOperator(
    entity: MigrationProcessingDataEntity
  ): string {
    return entity.conditionallyRequiredLogicalOperator || '';
  }

  // Parent-child relationship methods
  toggleChildrenExpanded(entity: MigrationProcessingDataEntity): void {
    if (entity && entity.hasChildFields) {
      entity.childrenExpanded = !entity.childrenExpanded;
    }
  }

  getChildEntities(parentId: string): MigrationProcessingDataEntity[] {
    return this.entities.filter(e => e.parentEntityId === parentId);
  }

  getTopLevelEntities(): MigrationProcessingDataEntity[] {
    return this.entities.filter(e => !e.parentEntityId);
  }

  // Get all visible entities considering parent-child hierarchy and expansion state
  getVisibleEntities(): MigrationProcessingDataEntity[] {
    const visible: MigrationProcessingDataEntity[] = [];

    const addEntityAndChildren = (entity: MigrationProcessingDataEntity) => {
      visible.push(entity);

      // If this entity has children and is expanded, add children recursively
      if (entity.hasChildFields && entity.childrenExpanded) {
        const children = this.getChildEntities(entity.id);
        children.forEach(child => addEntityAndChildren(child));
      }
    };

    // Start with top-level entities
    this.getTopLevelEntities().forEach(entity => addEntityAndChildren(entity));

    return visible;
  }

  getIndentStyle(entity: MigrationProcessingDataEntity): any {
    const indentLevel = entity.indentLevel || 0;
    return {
      'padding-left': indentLevel * 32 + 'px'
    };
  }

  isChildField(entity: MigrationProcessingDataEntity): boolean {
    return !!entity.parentEntityId;
  }

  getChildFieldClass(entity: MigrationProcessingDataEntity): string {
    if (!entity.parentEntityId) return '';
    const level = entity.indentLevel || 0;
    return `child-field child-field-level-${level}`;
  }
}
